/**
 * Schedule Calendar Filters (Phase 5 Vague B)
 *
 * ESM replacement for the 300-LOC inline ``{% include %}`` that used to
 * live in ``templates/office/schedule/_calendar_scripts.html`` (loaded
 * inside the bottom ``<script>`` of ``schedule_hub.html`` when
 * ``current_view == 'calendar'``).
 *
 * Provides the two top-of-grid autocomplete fields on the Calendar
 * view's toolbar:
 *
 *   - ``#cleaner-search``  → ``/office/api/cleaners/search/``
 *   - ``#customer-search`` → ``/office/api/customers/search/?include=all``
 *
 * Each search opens a dropdown of result items; clicking one writes
 * the picked id into the hidden ``#cleaner-filter`` / ``#customer-filter``
 * input and triggers ``window.applyCalendarFilters()`` (defined in
 * ``frontend/src/schedule-calendar.js``) so the FullCalendar grid
 * refetches with the new filter applied. A small floating "X" button
 * is rendered next to the input to clear the active filter.
 *
 * Wiring
 * ------
 * Auto-init via ``data-auto-init="scheduleCalendarFilters"`` placed on
 * the calendar view root (only present when ``current_view == 'calendar'``,
 * so the team view never picks this up). The ``X`` clear buttons in the
 * markup use ``data-clear-filter="cleaner|customer"`` instead of the
 * legacy ``onclick="clearCleanerFilter()"`` / ``onclick="clearCustomerFilter()"``
 * attributes — wired here via a single delegated click listener on the
 * calendar root.
 *
 * The customer status badge (on_hold / inactive) labels come from
 * ``window.CUSTOMER_PICKER_I18N`` (defined in ``base_office.html``)
 * to stay in sync with the rest of the office customer picker.
 */

import { customerAvatarHtml } from '../utils/ui/customer-avatar.js';

const MAX_SEARCH_RESULTS = 20;
const TR_DEFAULTS = {
  noCleanersMatching: 'No cleaners matching',
  noCustomersMatching: 'No customers matching',
};

function getTr(key) {
  return (window.TRANSLATIONS && window.TRANSLATIONS[key]) || TR_DEFAULTS[key] || key;
}

function escapeHtml(text) {
  if (window.ScheduleHub?.utils?.escapeHtml) {
    return window.ScheduleHub.utils.escapeHtml(text);
  }
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ============================================================
// Cleaner search
// ============================================================

function searchCleanersAPI(query) {
  const resultsContainer = document.getElementById('cleaner-search-results');
  if (!resultsContainer) {
    console.error('Cleaner search results container not found');
    return;
  }

  const apiUrl = '/office/api/cleaners/search/?q=' + encodeURIComponent(query) + '&limit=' + MAX_SEARCH_RESULTS;

  fetch(apiUrl)
    .then((response) => response.json())
    .then((data) => {
      if (data.results && data.results.length > 0) {
        let html = '';
        for (let i = 0; i < data.results.length; i++) {
          const cleaner = data.results[i];
          const initials = cleaner.initials || cleaner.name.substring(0, 2).toUpperCase();
          const escapedName = escapeHtml(cleaner.name);

          let avatarHtml;
          if (cleaner.photo_url) {
            avatarHtml = '<img src="' + cleaner.photo_url + '" alt="' + escapedName + '" class="w-7 h-7 rounded-full object-cover mr-2.5 flex-shrink-0">';
          } else {
            avatarHtml = '<div class="w-7 h-7 rounded-full flex items-center justify-center mr-2.5 flex-shrink-0 bg-primary-600">';
            avatarHtml += '<span class="text-xs font-medium text-white">' + initials + '</span>';
            avatarHtml += '</div>';
          }

          html += '<div class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex items-center cleaner-result-item" data-id="' + cleaner.id + '" data-name="' + escapedName + '">';
          html += avatarHtml;
          html += '<span class="text-sm font-medium text-gray-900 dark:text-white">' + escapedName + '</span>';
          html += '</div>';
        }
        resultsContainer.innerHTML = html;
      } else {
        resultsContainer.innerHTML = '<div class="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">' + getTr('noCleanersMatching') + ' "' + escapeHtml(query) + '"</div>';
      }
      resultsContainer.classList.remove('hidden');
    })
    .catch((error) => {
      console.error('Cleaner search error:', error);
      resultsContainer.classList.add('hidden');
    });
}

function showClearCleanerButton() {
  let btn = document.getElementById('clear-cleaner-btn');
  if (btn) return;
  const cleanerSearch = document.getElementById('cleaner-search');
  if (!cleanerSearch) return;
  const searchContainer = cleanerSearch.parentElement;
  btn = document.createElement('button');
  btn.type = 'button';
  btn.id = 'clear-cleaner-btn';
  btn.dataset.clearFilter = 'cleaner';
  btn.className = 'absolute inset-y-0 right-2 flex items-center justify-center w-6 h-6 my-auto rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 focus:outline-none';
  btn.innerHTML = '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/></svg>';
  searchContainer.appendChild(btn);
}

function hideClearCleanerButton() {
  const btn = document.getElementById('clear-cleaner-btn');
  if (btn) btn.remove();
}

function clearCleanerFilter() {
  const filter = document.getElementById('cleaner-filter');
  const search = document.getElementById('cleaner-search');
  const results = document.getElementById('cleaner-search-results');
  if (filter) filter.value = '';
  if (search) search.value = '';
  if (results) results.classList.add('hidden');
  hideClearCleanerButton();
  if (typeof window.applyCalendarFilters === 'function') window.applyCalendarFilters();
}

// ============================================================
// Customer search
// ============================================================

function searchCustomersAPI(query) {
  const resultsContainer = document.getElementById('customer-search-results');
  if (!resultsContainer) {
    console.error('Customer search results container not found');
    return;
  }

  // ``include=all`` so ON_HOLD and INACTIVE customers stay searchable —
  // their historical visits remain on the calendar and the office needs
  // to be able to filter on them. Status badges mirror the shared
  // customer-picker.js renderer.
  const apiUrl = '/office/api/customers/search/?q=' + encodeURIComponent(query) + '&limit=' + MAX_SEARCH_RESULTS + '&include=all';

  fetch(apiUrl)
    .then((response) => response.json())
    .then((data) => {
      if (data.error) {
        console.error('Customer API error:', data.error);
        resultsContainer.innerHTML = '<div class="px-3 py-2 text-red-500 text-sm">Error: ' + data.error + '</div>';
        resultsContainer.classList.remove('hidden');
        return;
      }

      if (data.results && data.results.length > 0) {
        const i18n = window.CUSTOMER_PICKER_I18N || {};
        let html = '';
        for (let i = 0; i < data.results.length; i++) {
          const customer = data.results[i];
          const escapedName = escapeHtml(customer.name);
          let badge = '';
          if (customer.status === 'on_hold') {
            badge = ' <span class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">'
              + escapeHtml(i18n.on_hold || 'On hold') + '</span>';
          } else if (customer.status === 'inactive') {
            badge = ' <span class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">'
              + escapeHtml(i18n.inactive || 'Inactive') + '</span>';
          }
          html += '<div class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex items-center customer-result-item" data-id="' + customer.id + '" data-name="' + escapedName + '">';
          html += customerAvatarHtml(customer.name, customer.customer_type);
          html += '<span class="text-sm font-medium text-gray-900 dark:text-white">' + escapedName + '</span>' + badge;
          html += '</div>';
        }
        resultsContainer.innerHTML = html;
      } else {
        resultsContainer.innerHTML = '<div class="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">' + getTr('noCustomersMatching') + ' "' + escapeHtml(query) + '"</div>';
      }
      resultsContainer.classList.remove('hidden');
    })
    .catch((error) => {
      console.error('Customer search error:', error);
      resultsContainer.innerHTML = '<div class="px-3 py-2 text-red-500 text-sm">Network error</div>';
      resultsContainer.classList.remove('hidden');
    });
}

function showClearCustomerButton() {
  let btn = document.getElementById('clear-customer-btn');
  if (btn) return;
  const customerSearch = document.getElementById('customer-search');
  if (!customerSearch) return;
  const searchContainer = customerSearch.parentElement;
  btn = document.createElement('button');
  btn.type = 'button';
  btn.id = 'clear-customer-btn';
  btn.dataset.clearFilter = 'customer';
  btn.className = 'absolute inset-y-0 right-2 flex items-center justify-center w-6 h-6 my-auto rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 focus:outline-none';
  btn.innerHTML = '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/></svg>';
  searchContainer.appendChild(btn);
}

function hideClearCustomerButton() {
  const btn = document.getElementById('clear-customer-btn');
  if (btn) btn.remove();
}

function clearCustomerFilter() {
  const filter = document.getElementById('customer-filter');
  const search = document.getElementById('customer-search');
  const results = document.getElementById('customer-search-results');
  if (filter) filter.value = '';
  if (search) search.value = '';
  if (results) results.classList.add('hidden');
  hideClearCustomerButton();
  if (typeof window.applyCalendarFilters === 'function') window.applyCalendarFilters();
}

// ============================================================
// Public init
// ============================================================

let _bootstrapped = false;
let _cleanerSearchTimeout = null;
let _customerSearchTimeout = null;

// These listeners are bound on ``document`` exactly once for the SPA
// lifetime (rule: global listeners ⇒ module-level bindOnce flag), so they
// stay live after the operator navigates away from the schedule hub. The
// Visits / Jobs / Cleaners list pages reuse the SAME ``#cleaner-search`` /
// ``#customer-search`` ids for their own SearchPicker autocompletes, so
// without a host-page guard these handlers would hijack those pickers —
// overwriting the dropdown with schedule rows and firing the stale
// ``window.applyCalendarFilters`` (= ``applyTeamFilters`` from the last
// team view), which redirected the Visits filter to /office/schedule/.
// The schedule hub always mounts ``#calendar`` (Calendar view) or
// ``#team-calendar`` (Team view); no other page does. Bail when neither
// is present so the module is fully inert off the schedule hub.
function isScheduleHubMounted() {
  return !!(document.getElementById('calendar') || document.getElementById('team-calendar'));
}

export function initScheduleCalendarFilters() {
  if (_bootstrapped) return;
  _bootstrapped = true;

  document.addEventListener('input', function onSearchInput(e) {
    if (!isScheduleHubMounted()) return;
    const id = e.target.id;
    if (id === 'cleaner-search') {
      clearTimeout(_cleanerSearchTimeout);
      const query = e.target.value.trim();
      if (query.length === 0) {
        const filterInput = document.getElementById('cleaner-filter');
        if (filterInput && filterInput.value) {
          filterInput.value = '';
          if (typeof window.applyCalendarFilters === 'function') window.applyCalendarFilters();
        }
      }
      _cleanerSearchTimeout = setTimeout(() => searchCleanersAPI(query), 300);
    } else if (id === 'customer-search') {
      clearTimeout(_customerSearchTimeout);
      const query = e.target.value.trim();
      if (query.length === 0) {
        const filterInput = document.getElementById('customer-filter');
        if (filterInput && filterInput.value) {
          filterInput.value = '';
          if (typeof window.applyCalendarFilters === 'function') window.applyCalendarFilters();
        }
      }
      _customerSearchTimeout = setTimeout(() => searchCustomersAPI(query), 300);
    }
  });

  document.addEventListener('focusin', function onSearchFocus(e) {
    if (!isScheduleHubMounted()) return;
    const id = e.target.id;
    if (id === 'cleaner-search') {
      const query = e.target.value.trim();
      const filterInput = document.getElementById('cleaner-filter');
      if (filterInput && filterInput.value) return;
      searchCleanersAPI(query);
    } else if (id === 'customer-search') {
      const query = e.target.value.trim();
      const filterInput = document.getElementById('customer-filter');
      if (filterInput && filterInput.value) return;
      searchCustomersAPI(query);
    }
  });

  document.addEventListener('click', function onResultOrClearClick(e) {
    if (!isScheduleHubMounted()) return;
    const cleanerItem = e.target.closest('.cleaner-result-item');
    if (cleanerItem) {
      const id = cleanerItem.dataset.id;
      const name = cleanerItem.dataset.name;
      const filter = document.getElementById('cleaner-filter');
      const search = document.getElementById('cleaner-search');
      const results = document.getElementById('cleaner-search-results');
      if (filter) filter.value = id;
      if (search) search.value = name;
      if (results) results.classList.add('hidden');
      showClearCleanerButton();
      if (typeof window.applyCalendarFilters === 'function') window.applyCalendarFilters();
      return;
    }

    const customerItem = e.target.closest('.customer-result-item');
    if (customerItem) {
      const id = customerItem.dataset.id;
      const name = customerItem.dataset.name;
      const filter = document.getElementById('customer-filter');
      const search = document.getElementById('customer-search');
      const results = document.getElementById('customer-search-results');
      if (filter) filter.value = id;
      if (search) search.value = name;
      if (results) results.classList.add('hidden');
      showClearCustomerButton();
      if (typeof window.applyCalendarFilters === 'function') window.applyCalendarFilters();
      return;
    }

    const clearBtn = e.target.closest('[data-clear-filter]');
    if (clearBtn) {
      const which = clearBtn.dataset.clearFilter;
      if (which === 'cleaner') clearCleanerFilter();
      else if (which === 'customer') clearCustomerFilter();
    }
  });

  // Close dropdowns when clicking outside
  document.addEventListener('click', function onClickOutside(e) {
    if (!isScheduleHubMounted()) return;
    const cleanerSearch = document.getElementById('cleaner-search');
    const cleanerResults = document.getElementById('cleaner-search-results');
    if (cleanerSearch && cleanerResults
        && !cleanerSearch.contains(e.target)
        && !cleanerResults.contains(e.target)
        && !e.target.closest('.cleaner-result-item')) {
      cleanerResults.classList.add('hidden');
    }

    const customerSearch = document.getElementById('customer-search');
    const customerResults = document.getElementById('customer-search-results');
    if (customerSearch && customerResults
        && !customerSearch.contains(e.target)
        && !customerResults.contains(e.target)
        && !e.target.closest('.customer-result-item')) {
      customerResults.classList.add('hidden');
    }
  });
}

export function autoInitScheduleCalendarFilters() {
  initScheduleCalendarFilters();
}
